/**
 * Spaces API service — the tenant's horizontal slices (product backend,
 * functions/spaces_api). Listing is visibility-aware server-side: restricted
 * spaces are omitted for callers without a grant in them, and the open
 * default space is bootstrapped on first access.
 */

import axios from 'axios';
import { SYSTEMDOX_API_URL } from '@/lib/apiConfig';

const api = axios.create({ baseURL: SYSTEMDOX_API_URL });

export type SpaceVisibility = 'open' | 'restricted';

export interface Space {
  spaceId: string;
  name: string;
  visibility: SpaceVisibility;
  category: string;
  sealed: boolean;
  archivedAt: string | null;
  isDefault: boolean;
  createdAt: string;
}

export const spacesApi = {
  setAuthProvider(getToken: () => Promise<string | null>) {
    api.interceptors.request.use(async (config) => {
      const token = await getToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
  },

  /** Spaces visible to the caller (any member). */
  async listSpaces(): Promise<Space[]> {
    const { data } = await api.get('/spaces');
    return data.spaces ?? [];
  },

  /** Create a space (admin+). Visibility defaults to restricted server-side. */
  async createSpace(input: { name: string; visibility?: SpaceVisibility }): Promise<Space> {
    const { data } = await api.post('/spaces', input);
    return data;
  },

  /** Rename / change visibility / archive-restore a space (admin+). The
   *  default space stays open and cannot be archived (server-enforced). */
  async updateSpace(
    spaceId: string,
    changes: { name?: string; visibility?: SpaceVisibility; archived?: boolean },
  ): Promise<Space> {
    const { data } = await api.patch(`/spaces/${spaceId}`, changes);
    return data;
  },
};
